import { Component, computed, effect, inject, signal } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  EcabSidebarComponent,
  EcabNavGroupComponent,
  EcabNavItemComponent,
  EcabUserMenuComponent
} from '@ecab/ecab-ui';
import { NotificationHostComponent } from './notifications/notification-host.component';
import { ApiClient, PredefinedMessageFilterDto, PredefinedMessageFilterDtoTargetView } from './services/api.clients.generated';
import { FILTER_ID_QUERY_PARAM, routeForPredefinedFilter } from './messages/message-filter-state';
import { NotificationService } from './notifications/notification.service';
import { AuthStateService } from './auth/auth-state.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    EcabSidebarComponent,
    EcabNavGroupComponent,
    EcabNavItemComponent,
    EcabUserMenuComponent,
    NotificationHostComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  router = inject(Router);
  authState = inject(AuthStateService);
  backofficeApi = inject(ApiClient);
  notificationService = inject(NotificationService);
  isAuthenticated = computed(() => this.authState.isAuthenticated());
  sidebarCollapsed = false;
  currentUrl = this.router.url;
  predefinedFilters = signal<PredefinedMessageFilterDto[]>([]);
  messageSidebarFilters = computed(() =>
    this.predefinedFilters().filter(filter => filter.targetView === PredefinedMessageFilterDtoTargetView.MESSAGES)
  );
  openMessageSidebarFilters = computed(() =>
    this.predefinedFilters().filter(filter => filter.targetView === PredefinedMessageFilterDtoTargetView.OPEN_MESSAGES)
  );

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.currentUrl = (event as NavigationEnd).urlAfterRedirects;
    });

    effect(() => {
      const resolved = this.authState.isResolved();
      const account = this.authState.activeAccount();

      if (!resolved || !account) {
        this.predefinedFilters.set([]);
        return;
      }

      void this.loadPredefinedFilters();
    });

    void this.authState.initialize();
  }

  get userName(): string {
    const account = this.authState.activeAccount();
    return account?.name || 'Utilisateur';
  }

  get userEmail(): string {
    const account = this.authState.activeAccount();
    return account?.username || '';
  }

  logout() {
    this.authState.logout();
  }

  navigate(route: string) {
    this.router.navigate([route]);
  }

  navigateToPredefinedFilter(filter: PredefinedMessageFilterDto) {
    const target = routeForPredefinedFilter(filter);
    this.router.navigate([target.path], { queryParams: target.queryParams });
  }

  isParentRouteActive(route: '/messages' | '/open-messages'): boolean {
    return this.router.parseUrl(this.currentUrl).root.children['primary']?.segments.map(segment => segment.path).join('/') === route.slice(1)
      || this.currentUrl.startsWith(`${route}/`);
  }

  isSidebarFilterActive(filter: PredefinedMessageFilterDto): boolean {
    const target = routeForPredefinedFilter(filter);
    const urlTree = this.router.parseUrl(this.currentUrl);
    const currentPath = `/${urlTree.root.children['primary']?.segments.map(segment => segment.path).join('/') ?? ''}`;
    const currentFilterId = urlTree.queryParams[FILTER_ID_QUERY_PARAM];

    return currentPath === target.path && currentFilterId === filter.identifier;
  }

  private async loadPredefinedFilters() {
    try {
      const filters = await firstValueFrom(this.backofficeApi.getPredefinedMessageFilters());
      this.predefinedFilters.set(filters ?? []);
    } catch (error) {
      this.predefinedFilters.set([]);
      this.notificationService.warning(
        this.notificationService.errorMessage(error, 'Les filtres prédéfinis du menu ne sont pas disponibles pour le moment.')
      );
    }
  }
}
